import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const formSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Please enter a valid email address'),
  company: z.string().optional(),
  projectType: z.string().min(1, 'Please select a plan'),
  description: z.string().optional(),
  timeline: z.string().min(1, 'Please select your preferred timeline'),
});

type FormData = z.infer<typeof formSchema>;

export const ContactFormSection = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();
  const { toast } = useToast();
  
  // Get URL params to preselect plan
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedPlan = urlParams.get('plan');
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectType: preselectedPlan || undefined
    }
  });

  // Set the preselected plan when component mounts
  React.useEffect(() => {
    if (preselectedPlan) {
      setValue("projectType", preselectedPlan);
    }
  }, [preselectedPlan, setValue]);

  const projectType = watch('projectType');
  const timeline = watch('timeline');

  const onSubmit = async (data: FormData) => {
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Message sent!",
        description: "We'll reply within 24 hours from post@affinityaim.com",
      });
      
      reset();
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="section">
      <div className="container-professional">
        {/* Header */}
        <div className="section-header-professional">
          <div className="micro-label-professional">CONTACT</div>
          <h2 className="heading-section">
            Let's build your app idea
          </h2>
          <p className="text-body max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Tell us about your project and we'll provide a detailed proposal within 24 hours
          </p>
        </div>

        <div ref={elementRef} className="max-w-2xl mx-auto">
          <form 
            onSubmit={handleSubmit(onSubmit)} 
            className="bg-[#111111] border border-[rgba(255,255,255,0.15)] rounded-xl p-8 space-y-6"
            style={getRevealStyle(0)}
          >
            {/* Name & Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-white">
                  Name *
                </Label>
                <Input
                  id="name"
                  {...register("name")}
                  className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white"
                  placeholder="Your name"
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-xs text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-white">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email")}
                  className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white"
                  placeholder="your@email.com"
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="text-xs text-red-400">{errors.email.message}</p>
                )}
              </div>
            </div>

            {/* Company */}
            <div className="space-y-2">
              <Label htmlFor="company" className="text-sm font-medium text-white">
                Company name
              </Label>
              <Input
                id="company"
                {...register("company")}
                className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white"
                placeholder="Your company"
              />
            </div>

            {/* Project Type */}
            <div className="space-y-2">
              <Label htmlFor="projectType" className="text-sm font-medium text-white">
                Which plan interests you? *
              </Label>
              <Select value={projectType} onValueChange={(value) => setValue("projectType", value)}>
                <SelectTrigger className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-[rgba(255,255,255,0.15)]">
                  <SelectItem value="Website Essentials ($500/year)">Website Essentials ($500/year)</SelectItem>
                  <SelectItem value="Growth Website + SEO ($800/year)">Growth Website + SEO ($800/year)</SelectItem>
                  <SelectItem value="AI Chatbot ($500/year)">AI Chatbot ($500/year)</SelectItem>
                  <SelectItem value="Website + SEO + Chatbot ($1,000/year)">Website + SEO + Chatbot ($1,000/year)</SelectItem>
                  <SelectItem value="Custom App / Web App / MVP (Custom)">Custom App / Web App / MVP (Custom)</SelectItem>
                  <SelectItem value="help-decide">Help me decide</SelectItem>
                </SelectContent>
              </Select>
              {errors.projectType && (
                <p id="project-type-error" className="text-xs text-red-400">{errors.projectType.message}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-white">
                Brief description of your goals
              </Label>
              <Textarea
                id="description"
                {...register("description")}
                className="text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] min-h-[100px] transition-all resize-none text-white"
                placeholder="Tell us what you're trying to achieve..."
              />
            </div>

            {/* Timeline */}
            <div className="space-y-2">
              <Label htmlFor="timeline" className="text-sm font-medium text-white">
                Preferred timeline *
              </Label>
              <Select onValueChange={(value) => setValue("timeline", value)}>
                <SelectTrigger className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white">
                  <SelectValue placeholder="Select timeline" />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-[rgba(255,255,255,0.15)]">
                  <SelectItem value="asap">ASAP</SelectItem>
                  <SelectItem value="1-2-months">1-2 months</SelectItem>
                  <SelectItem value="3-6-months">3-6 months</SelectItem>
                  <SelectItem value="just-exploring">Just exploring</SelectItem>
                </SelectContent>
              </Select>
              {errors.timeline && (
                <p id="timeline-error" className="text-xs text-red-400">{errors.timeline.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 text-sm bg-[#00e5ff] hover:bg-[#00d4e6] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-all duration-200 hover:transform hover:-translate-y-0.5"
              >
                <span>{isSubmitting ? "Sending..." : "Send message"}</span>
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
            </div>

            {/* Reply info */}
            <p className="text-xs text-center" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              We'll reply within 24 hours from <span style={{ color: '#00e5ff' }}>post@affinityaim.com</span>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};